import * as RadixSelect from '@radix-ui/react-select';
import clsx from 'clsx';
import { Check, ChevronDown, type LucideIcon } from 'lucide-react';
import { Icon } from '../display/Icon.tsx';
import styles from './MenuSelect.module.css';
import listStyles from './Select.module.css';

export interface MenuSelectOption<V extends string> {
  readonly value: V;
  readonly label: string;
  /** Shown instead of the label in the trigger on narrow screens, e.g. a language code. */
  readonly short?: string | undefined;
}

export interface MenuSelectProps<V extends string> {
  /** Accessible name of the control (it has no visible field label). */
  readonly label: string;
  readonly icon?: LucideIcon | undefined;
  readonly value: V;
  readonly options: readonly MenuSelectOption<V>[];
  readonly onChange: (value: V) => void;
  readonly className?: string | undefined;
}

/**
 * Compact choice-of-one for toolbars and headers: shows the current value, opens a list.
 * The list is the same one the form Select uses.
 */
export const MenuSelect = <V extends string>({
  label,
  icon,
  value,
  options,
  onChange,
  className,
}: MenuSelectProps<V>) => {
  const current = options.find((option) => option.value === value);
  return (
    <RadixSelect.Root
      value={value}
      onValueChange={(next) => {
        const option = options.find((candidate) => candidate.value === next);
        if (option) onChange(option.value);
      }}
    >
      <RadixSelect.Trigger className={clsx(styles.trigger, className)} aria-label={label}>
        {icon && <Icon icon={icon} size="sm" />}
        <span className={styles.full}>{current?.label}</span>
        <span className={styles.short} aria-hidden="true">
          {current?.short ?? current?.label}
        </span>
        <RadixSelect.Icon className={styles.chevron}>
          <Icon icon={ChevronDown} size="sm" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className={clsx(listStyles.content, styles.content)}
          position="popper"
          align="end"
          sideOffset={6}
          collisionPadding={8}
        >
          <RadixSelect.Viewport className={listStyles.viewport}>
            {options.map((option) => (
              <RadixSelect.Item key={option.value} value={option.value} className={listStyles.item}>
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className={listStyles.indicator}>
                  <Icon icon={Check} size="sm" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};
